import { type MonoTypeOperatorFunction, retry, timer } from 'rxjs';
import { TRANSIENT_CODES } from '../firebase/failure-permanence.ts';

// A failure is transient when it's expected to resolve on its own if we
// wait and try again. Which codes those are is `failure-permanence.ts`'s to
// say; what this file adds is reach, because different clients surface the
// same underlying infrastructure flake on different fields:
//
//   numeric:  408/425/429 (client-retryable) + 500/502/503/504 (server)
//   numeric:  gRPC codes 4/8/10/13/14 (deadline/exhausted/aborted/internal/unavailable)
//   string:   the gRPC code names — accepted in any casing and either
//             separator (UNAVAILABLE, unavailable, RESOURCE_EXHAUSTED,
//             resource-exhausted), normalized back to the canonical spelling.
//
// Every client puts these on some mix of .status / .code / .statusCode,
// sometimes buried on .cause. We extract from all those slots.
const TRANSIENT_HTTP_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
const TRANSIENT_GRPC_CODE = new Set([4, 8, 10, 13, 14]);

function extractCodes(obj: unknown): Array<string | number> {
  if (!obj || typeof obj !== 'object') return [];
  const e = obj as Record<string, unknown>;
  const out: Array<string | number> = [];
  for (const key of ['status', 'statusCode', 'code']) {
    const v = e[key];
    if (typeof v === 'number' || typeof v === 'string') out.push(v);
  }
  return out;
}

function isTransient(err: unknown): boolean {
  const cause = typeof err === 'object' && err !== null && 'cause' in err ? err.cause : undefined;
  // A source that already spent its recovery budget keeps that decision across outer retries.
  if (
    [err, cause].some(
      (error) =>
        typeof error === 'object' &&
        error !== null &&
        'retryable' in error &&
        error.retryable === false,
    )
  )
    return false;
  const candidates = [...extractCodes(err), ...extractCodes(cause)];
  return candidates.some((v) => {
    if (typeof v === 'number') {
      return TRANSIENT_HTTP_STATUS.has(v) || TRANSIENT_GRPC_CODE.has(v);
    }
    return TRANSIENT_CODES.has(v.toLowerCase().replace(/_/g, '-'));
  });
}

/**
 * Retry on transient failures only. Apply to every non-deterministic
 * async step in a pipe — Firestore calls, HTTP fetches, LLM invocations.
 *
 * Non-transient errors short-circuit immediately — the operator never
 * swallows signal errors, only waits out infrastructure flakes.
 *
 * Logs every retry attempt to stderr via `console.warn` so an operator
 * tailing logs can confirm whether the retry fired (and how many times)
 * when investigating a failure.
 *
 * Policy: up to 3 retries, exponential backoff (500ms * 2^attempt) plus
 * 0–250ms jitter. Tune by wrapping this into a caller-specific operator
 * if a step legitimately needs a different posture (e.g., LLM 503s that
 * persist for minutes may warrant longer backoff).
 */
export function retryOnTransient<T>(): MonoTypeOperatorFunction<T> {
  return retry({
    count: 3,
    delay: (err: unknown, attempt) => {
      if (!isTransient(err)) throw err;
      const wait = 2 ** attempt * 500 + Math.random() * 250;
      const e = err as {
        status?: unknown;
        code?: unknown;
        message?: unknown;
      };
      const signal = e.status ?? e.code ?? e.message ?? 'unknown';
      console.warn(
        `[retryOnTransient] attempt ${attempt} after ${Math.round(wait)}ms: ${String(signal)}`,
      );
      return timer(wait);
    },
  });
}
